"use client";

import { useEffect, useRef } from "react";
import { sendClientErrorReport } from "@/lib/client-error-report";

/**
 * Captura erros que NENHUMA Error Boundary pega:
 *  - `window.onerror`: erro em callback/evento fora do React (ex.: um `onClick`
 *    que lança, um `setTimeout`, um listener de socket).
 *  - `unhandledrejection`: promise rejeitada sem `catch`. O caso mais comum em
 *    produto: um `fetch` que ninguém aguarda.
 *
 * Erros dentro da árvore React são tratados pelas boundaries (`app/error.tsx` /
 * `app/global-error.tsx`) e NÃO chegam aqui — o React os intercepta antes, o
 * que evita alerta duplicado.
 *
 * Não renderiza nada. Montado uma vez no `app/layout.tsx`.
 */
export function ErrorReporter() {
  // Listener registrado uma única vez por mount. Guardar em ref evita
  // re-registrar a cada render, que geraria o par add/remove sem fim e
  // deixaria o array de deps instável (mesmo anti-padrão documentado no
  // AGENTS.md para SSE).
  const mountedRef = useRef(false);

  useEffect(() => {
    if (mountedRef.current) return;
    mountedRef.current = true;

    const onError = (event: ErrorEvent) => {
      // Erro de recurso (`<img>`, `<script>`) chega aqui sem `Error`:
      // `event.error` é null e o detalhe está em `event.target`.
      const target = event.target as
        | (EventTarget & { src?: string; href?: string })
        | null;

      if (!event.error && target && (target.src || target.href)) {
        sendClientErrorReport({
          message: `Falha ao carregar recurso: ${target.src || target.href}`,
          context: { tipo: "resource" },
        });
        return;
      }

      sendClientErrorReport({
        message: event.message || "Erro não identificado",
        stack: event.error instanceof Error ? event.error.stack : undefined,
        context: {
          tipo: "window.onerror",
          ...(event.filename ? { arquivo: event.filename } : {}),
          ...(typeof event.lineno === "number" ? { linha: String(event.lineno) } : {}),
        },
      });
    };

    const onRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      const isError = reason instanceof Error;

      sendClientErrorReport({
        message: isError ? reason.message : `Promise rejeitada: ${safeStringify(reason)}`,
        stack: isError ? reason.stack : undefined,
        context: { tipo: "unhandledrejection" },
      });
    };

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);

    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
      mountedRef.current = false;
    };
  }, []);

  return null;
}

function safeStringify(value: unknown): string {
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value) ?? String(value);
  } catch {
    return String(value);
  }
}
